/**
 * Spreadsheet reader (.xlsx) on SheetJS Community Edition
 * (Apache-2.0, the brief's recommended library), loaded lazily
 * through a dynamic import. Reads the first worksheet unless a sheet
 * is named; additional sheets are reported as an issue so nothing is
 * dropped silently (brief P4).
 *
 * Cell rules: numbers stay numbers; text stays text; booleans become
 * the text 'TRUE'/'FALSE'; date cells become ISO text (a cell storing
 * a bare calendar date becomes 'YYYY-MM-DD', a cell storing a time of
 * day becomes a full ISO-8601 timestamp carrying the stored
 * wall-clock time) and a column of nothing but dates is typed 'date'.
 * Column typing then follows the same rule as delimited text.
 *
 * Timezone rule: a spreadsheet date is a calendar value, so its text
 * must not depend on the process timezone. SheetJS's cellDates Date
 * objects fail that rule twice over — toISOString() renders the UTC
 * instant (shifting the calendar day outside UTC), and in zones whose
 * historical UTC offset has a seconds component the Date itself lands
 * seconds off the stored wall time. The reader therefore recovers
 * date components from the stored serial number through
 * SSF.parse_date_code (pure arithmetic, identical in every timezone),
 * via a second parse with cellDates off whenever the first parse
 * produced any date cell. See OPEN_QUESTIONS Q23.
 */
import { IoError, parseFailure } from './errors.js';
import { NO_USER_MISSING } from './types.js';
let xlsxPromise = null;
function loadXlsx() {
    xlsxPromise ??= import('xlsx');
    return xlsxPromise;
}
const pad = (n, w = 2) => String(n).padStart(w, '0');
/**
 * ISO text for a date cell, from its stored components: a bare
 * calendar date when the stored time is exactly midnight, otherwise
 * the stored wall-clock time as a full ISO-8601 timestamp. Pure
 * arithmetic on the components — no Date, no timezone.
 */
function isoFromParts(p) {
    let { y, m, d } = p;
    let msInDay = ((p.H * 60 + p.M) * 60 + p.S) * 1000 + Math.round(p.u * 1000);
    if (msInDay >= 86_400_000) {
        // Sub-second rounding carried past midnight; roll the date with
        // UTC calendar arithmetic (timezone-free by construction).
        msInDay -= 86_400_000;
        const next = new Date(Date.UTC(y, m - 1, d + 1));
        y = next.getUTCFullYear();
        m = next.getUTCMonth() + 1;
        d = next.getUTCDate();
    }
    const date = `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
    if (msInDay === 0)
        return date;
    const hh = Math.floor(msInDay / 3_600_000);
    const mm = Math.floor(msInDay / 60_000) % 60;
    const ss = Math.floor(msInDay / 1_000) % 60;
    const ms = msInDay % 1_000;
    return `${date}T${pad(hh)}:${pad(mm)}:${pad(ss)}.${pad(ms, 3)}Z`;
}
/**
 * Fallback for a date cell whose stored serial could not be recovered
 * (not expected: a Date in the first parse implies a serial in the
 * second). Formats the Date's local components, which are the stored
 * wall-clock values in ordinary zones.
 */
function isoFromDateFallback(d) {
    return isoFromParts({
        y: d.getFullYear(),
        m: d.getMonth() + 1,
        d: d.getDate(),
        H: d.getHours(),
        M: d.getMinutes(),
        S: d.getSeconds(),
        u: d.getMilliseconds() / 1000,
    });
}
export async function readSpreadsheetTable(bytes, options = {}) {
    const XLSX = await loadXlsx();
    const issues = [];
    let wb;
    try {
        wb = XLSX.read(bytes, { type: 'array', cellDates: true });
    }
    catch (cause) {
        throw parseFailure('Excel .xlsx', cause);
    }
    if (wb.SheetNames.length === 0) {
        throw new IoError({
            code: 'IO_EMPTY_FILE',
            severity: 'error',
            message: 'This workbook has no worksheets. Choose a file with a data sheet.',
        });
    }
    let sheetName;
    if (typeof options.sheet === 'string') {
        if (!wb.SheetNames.includes(options.sheet)) {
            throw new IoError({
                code: 'IO_SHEET_NOT_FOUND',
                severity: 'error',
                message: `This workbook has no sheet named "${options.sheet}". Sheets found: ${wb.SheetNames.join(', ')}.`,
            });
        }
        sheetName = options.sheet;
    }
    else {
        sheetName = wb.SheetNames[options.sheet ?? 0] ?? wb.SheetNames[0];
    }
    if (wb.SheetNames.length > 1) {
        issues.push({
            code: 'IO_EXTRA_SHEETS',
            severity: 'warning',
            message: `This workbook has ${wb.SheetNames.length} sheets; only "${sheetName}" was read. Sheets not read: ${wb.SheetNames.filter((s) => s !== sheetName).join(', ')}.`,
        });
    }
    const sheetGrid = (book) => XLSX.utils.sheet_to_json(book.Sheets[sheetName], {
        header: 1,
        raw: true,
        defval: null,
        blankrows: false,
    });
    const grid = sheetGrid(wb);
    if (grid.length === 0) {
        throw new IoError({
            code: 'IO_EMPTY_FILE',
            severity: 'error',
            message: `The sheet "${sheetName}" is empty. Choose a sheet with a header row and at least one data row.`,
        });
    }
    const header = grid[0];
    const body = grid.slice(1);
    if (body.length === 0) {
        throw new IoError({
            code: 'IO_NO_DATA_ROWS',
            severity: 'error',
            message: `The sheet "${sheetName}" has a header row but no data rows.`,
        });
    }
    const names = header.map((h, i) => {
        if (h === null || h === undefined || String(h).trim() === '') {
            issues.push({
                code: 'IO_UNNAMED_COLUMN',
                severity: 'warning',
                message: `Column ${i + 1} has no header; it was named V${i + 1}. Rename it in the file if it should carry data.`,
                column: `V${i + 1}`,
            });
            return `V${i + 1}`;
        }
        return String(h).trim();
    });
    const seen = new Map();
    for (const name of names) {
        const count = (seen.get(name) ?? 0) + 1;
        seen.set(name, count);
        if (count === 2) {
            issues.push({
                code: 'IO_DUPLICATE_COLUMN',
                severity: 'warning',
                message: `The column name "${name}" appears more than once. Column references use the first one; rename the duplicates in the file to use them.`,
                column: name,
            });
        }
    }
    // Timezone rule (header comment, Q23): when the first parse
    // produced any date cell, recover each date's stored components
    // from its serial number in a second parse with cellDates off.
    // cellDates changes only the value conversion, so both grids
    // compact rows identically and align position for position.
    // Indexed by grid coordinates (row 0 is the header).
    let dateParts = null;
    if (grid.some((row) => row.some((c) => c instanceof Date))) {
        let rawWb;
        try {
            rawWb = XLSX.read(bytes, { type: 'array', cellDates: false });
        }
        catch (cause) {
            throw parseFailure('Excel .xlsx', cause);
        }
        const date1904 = rawWb.Workbook?.WBProps?.date1904 === true;
        const parseDateCode = XLSX.SSF.parse_date_code;
        dateParts = sheetGrid(rawWb).map((row) => row.map((c) => typeof c === 'number' ? parseDateCode(c, { date1904 }) : null));
    }
    const width = names.length;
    const rows = [];
    const wasDate = [];
    for (let r = 0; r < body.length; r++) {
        const row = body[r];
        const outRow = [];
        const dateRow = [];
        for (let i = 0; i < width; i++) {
            const cell = row[i] ?? null;
            if (cell === null || cell === undefined) {
                outRow.push(null);
                dateRow.push(false);
            }
            else if (cell instanceof Date) {
                const parts = dateParts?.[r + 1]?.[i] ?? null;
                outRow.push(parts ? isoFromParts(parts) : isoFromDateFallback(cell));
                dateRow.push(true);
            }
            else if (typeof cell === 'number') {
                outRow.push(Number.isFinite(cell) ? cell : null);
                dateRow.push(false);
            }
            else if (typeof cell === 'boolean') {
                outRow.push(cell ? 'TRUE' : 'FALSE');
                dateRow.push(false);
            }
            else {
                const s = String(cell);
                outRow.push(s === '' ? null : s);
                dateRow.push(false);
            }
        }
        rows.push(outRow);
        wasDate.push(dateRow);
    }
    const columns = names.map((name, i) => {
        const cells = rows.map((r) => r[i]);
        const nonNullIdx = cells
            .map((c, r) => (c !== null ? r : -1))
            .filter((r) => r >= 0);
        const allDates = nonNullIdx.length > 0 && nonNullIdx.every((r) => wasDate[r][i]);
        const allNumbers = nonNullIdx.length > 0 &&
            nonNullIdx.every((r) => typeof cells[r] === 'number');
        return {
            name,
            label: null,
            type: allDates ? 'date' : allNumbers ? 'number' : 'string',
            storageType: null,
            displayFormat: null,
            measure: 'unknown',
            valueLabels: [],
            userMissing: NO_USER_MISSING,
            ...(allDates ? { dateEpoch: 'iso-text' } : {}),
            provenance: 'inferred',
        };
    });
    const table = { format: 'xlsx', fileLabel: null, columns, rows };
    return { table, issues };
}
//# sourceMappingURL=spreadsheet.js.map